// src/api/slice.ts
import { api } from "./client";
import { AxiosError, Method } from "axios";

import router from "@/router";
import { useAuthStore } from "@/stores/auth";
import { useUserStore } from "@/stores/user";

export interface ResponseMeta {
  error: any;
  status: number;
}

export interface ResponseModel<T = any, E = any> {
  meta: ResponseMeta;
  data: T;
}

export interface RequestOptions {
  auth?: boolean;
  headers?: Record<string, string>;
}

export default class ApiSlice {
  static domenURL = "";
  static defaultAuth = false;

  static async request<T = unknown, E = unknown>(
    url = "",
    method: Method = "GET",
    payload: Record<string, unknown> | FormData | null = null,
    options: RequestOptions = {},
    _baseUrlArg?: string,
    domenUrlArg?: string
  ): Promise<ResponseModel<T, E>> {
    let headers: Record<string, string> = {};

    let accessToken = localStorage.getItem("access_token");

    // Telegram storage fallback
    if (!accessToken && window.Telegram?.WebApp?.storage) {
      try {
        accessToken = await window.Telegram.WebApp.storage.getItem("access_token");
        if (accessToken) {
          console.log("🔑 Token взят из Telegram storage");
        }
      } catch (e) {
        console.warn("Не удалось получить token из Telegram storage:", e);
      }
    }

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken.replace(/^Bearer\s+/i, "")}`;
      console.log("🔑 Используем Bearer access_token");
    } else {
      console.warn("⚠️ Нет access_token — защищённый запрос будет отклонён (401)");
    }

    if (options?.headers) {
      headers = { ...headers, ...options.headers };
    }

    try {
      const res = await api.request<ResponseModel<T, E>>({
        method,
        url: (domenUrlArg ?? this.domenURL) + url,
        data: payload || undefined,
        headers,
      });

      return res.data;
    } catch (err) {
      const axiosErr = err as AxiosError<ResponseModel<T, E>>;
      const response = axiosErr.response?.data;

      // MTS status uses Login-Token, so its 401 only invalidates the temporary
      // phone-auth session and must not trigger the global account logout.
      if (url === "/mts/status" && axiosErr.response?.status === 401) {
        throw axiosErr;
      }

      if (response?.meta?.status === 401) {
        console.warn("🔐 401 — сервер вернул 401");

        const auth = useAuthStore();
        const user = useUserStore();

        const tokenExists = !!localStorage.getItem("access_token");

        if (tokenExists) {
          console.warn("⚠️ 401, но токен существует → не выполняем logout (возможно временная ошибка)");
          throw axiosErr;
        }

        console.warn("❗ Токена нет → выполняем logout");
        try {
          auth.clear();
          user.clearUser();
        } catch (e) {
          console.warn("Ошибка при logout:", e);
        }

        router.replace("/");
        throw axiosErr;
      }

      if (response?.meta?.error) {
        throw axiosErr;
      }

      if (!response) {
        throw new Error("Server unreachable");
      }

      throw axiosErr;
    }
  }
}
